/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';

import envConfig from '../../../config/envConfig';
import { checkInApi } from '../../../hooks/useCheckInDesk';
import { countRows, fetchRows } from '../checkin/checkInApi';
import { idOf } from '../checkin/checkInRules';
import { saveBatch } from '../checkin/certificatesTab';
import { deleteUploadedFile, uploadPendingFile } from '../../common/FileUploadInput';
import {
  CERTIFICATE_CHOICE_TABLE, CERTIFICATE_TEMPLATE_TABLE, ISSUED_CERTIFICATE_TABLE, ORGANIZATION_NAME_MAX, ORGANIZATION_TABLE,
  certificateBatchOperations, certificateFormErrors, certificateOf, certificateStatus, certificatesEditable,
  deleteLogoOperations, editCertificate, hydrateCertificates, isDeletedLogo, isSavedEvent, liveLogo, orgChanged, orgFromRows,
} from '../../athlohive/eventCertificates';
import { previewValues } from '../../athlohive/certificateFiller';
import CertificateRender, { PAGE } from '../../athlohive/CertificateRender';
import { useCertificateMap, withCertificateMap } from '../../athlohive/certificateMap';

const LOGO_MAX_BYTES = 1024 * 1024;
// The upload and delete loaders stay up at least this long, so the step is
// visible even when storage answers at once.
export const LOGO_LOADER_MS = 3000;
const atLeast = (promise, ms) => Promise.all([promise, new Promise((resolve) => { setTimeout(resolve, ms); })])
  .then(([result]) => result);

const equals = (column, value) => ({ column_key: column, filter_type: 'equals', column_value: value });

const STATUS = {
  issued: { label: 'Certificates issued', icon: 'bi-patch-check-fill', tone: 'locked' },
  saved: { label: 'Saved', icon: 'bi-check-circle-fill', tone: 'saved' },
  ready: { label: 'Ready to save', icon: 'bi-circle-fill', tone: 'ready' },
  none: { label: 'No certificate', icon: 'bi-dash-circle', tone: 'none' },
};

/** One row by id, through the batch read (table/fetch cannot filter on id). */
async function fetchRowById(api, table, id) {
  const resp = await api.apiCall('/arcee/application/table/read/records/', {
    method: 'POST',
    body: JSON.stringify({
      organization_id: Number(envConfig.organizationId),
      application_id: Number(envConfig.applicationId),
      operations: [{ ref: 'r', table, where: { id } }],
    }),
  });
  if (!resp.ok) throw new Error(`Reading ${table} failed.`);
  const body = await resp.json();
  const block = (body?.results || body)?.r;
  return block && block.status === 'ok' && Array.isArray(block.data) ? block.data[0] || null : null;
}

/** The activity's course or venue in the form, for its certificate map. */
const courseOf = (activity) => ({
  routeLine: activity?.route?.route_polyline || null,
  venue: activity?.venue?.venue_point || null,
  venueName: activity?.venue?.venue_name || '',
});

/** Inside the organizer shell, where the theme's colour tokens are defined. */
const shell = () => document.querySelector('.org-shell') || document.body;

function useActiveTemplates() {
  return useQuery({
    queryKey: ['certificateTemplates', 'active'],
    queryFn: async () => {
      const rows = await fetchRows(checkInApi, CERTIFICATE_TEMPLATE_TABLE, {
        filters: [equals('is_active', true)], pageSize: 200,
      });
      return rows
        .map((row) => ({ id: String(row.id), name: String(row.name || ''), description: row.description || '', url: row.template_url || '' }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

// Room the preview dialog takes around the certificate: padding and border
// across, and the header and footer rows down (see .orgc-bigpreview).
const PREVIEW_CHROME = { x: 48, y: 170 };

/**
 * The largest width at which the whole A4 page fits the window, both ways: a
 * landscape page on a wide but short screen is limited by the height.
 */
export function fitPreviewWidth(viewport = { width: window.innerWidth, height: window.innerHeight }) {
  const byWidth = viewport.width - 40 - PREVIEW_CHROME.x;
  const byHeight = (viewport.height * 0.94 - PREVIEW_CHROME.y) * (PAGE.width / PAGE.height);
  return Math.max(240, Math.floor(Math.min(PAGE.width, byWidth, byHeight)));
}

/**
 * The certificate at full size, as a participant will get it: the organizer's
 * event, activity, name and logo, with a sample participant. Sized to the
 * window, never larger than the page itself.
 */
function CertificatePreview({ template, values, subtitle, onClose, onUse }) {
  const [width, setWidth] = useState(fitPreviewWidth);
  useEffect(() => {
    const onResize = () => setWidth(fitPreviewWidth());
    const onKey = (event) => { if (event.key === 'Escape') { event.stopPropagation(); onClose(); } };
    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKey, true);
    return () => { window.removeEventListener('resize', onResize); window.removeEventListener('keydown', onKey, true); };
  }, [onClose]);

  return createPortal(
    <div className="ochd-scrim orgc-previewscrim" role="presentation" onClick={onClose}>
      <div
        className="ochd orgc-bigpreview" role="dialog" aria-modal="true"
        aria-label={`${template.name} preview`} onClick={(e) => e.stopPropagation()}
        style={{ width: width + PREVIEW_CHROME.x }}
      >
        <header className="ochd-head">
          <div>
            <h2>{template.name}</h2>
            <p className="orgc-dim">{subtitle}</p>
          </div>
          <button type="button" className="ochk-iconbtn" onClick={onClose} aria-label="Close preview">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>
        <CertificateRender template={template} width={width} values={values} />
        <div className="orgc-bigpreview-foot">
          <span className="orgc-hint">
            <i className="bi bi-info-circle" aria-hidden="true" /> Sample participant <b>{values.name}</b> and certificate number shown for preview.
          </span>
          <div className="orgc-bigpreview-actions">
            <button type="button" className="org-btn ghost" onClick={onClose}>Close</button>
            {onUse && <button type="button" className="org-btn" onClick={onUse}><i className="bi bi-check2" aria-hidden="true" /> Use this template</button>}
          </div>
        </div>
      </div>
    </div>,
    shell(),
  );
}

/**
 * The organization on every certificate of this event: name and logo, read
 * from the event's organization row. Name and a new logo are saved with Save
 * draft / Publish; Delete is immediate.
 */
function OrganizationCard({ org, readOnly, errors, onChange, onDelete }) {
  const input = useRef(null);
  const [state, setState] = useState({ busy: '', error: '' }); // busy: '' | 'upload' | 'delete'
  const logo = liveLogo(org.logoUrl);
  const unsavedLogo = org.logoUrl !== org.savedLogoUrl;

  const pick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!(file.type || '').startsWith('image/')) { setState({ busy: '', error: 'Choose an image: PNG, JPG or SVG.' }); return; }
    if (file.size > LOGO_MAX_BYTES) { setState({ busy: '', error: 'The logo must be 1 MB or smaller.' }); return; }
    setState({ busy: 'upload', error: '' });
    try {
      const { url } = await atLeast(uploadPendingFile(file, { is_public: true, fileType: 'image' }), LOGO_LOADER_MS);
      if (!url) throw new Error('The upload gave no link.');
      // A logo uploaded earlier in this visit and never saved is replaced: remove its file.
      if (unsavedLogo && liveLogo(org.logoUrl)) deleteUploadedFile(org.logoUrl);
      onChange({ logoUrl: url });
      setState({ busy: '', error: '' });
    } catch (err) {
      setState({ busy: '', error: err.message || 'Upload failed.' });
    }
  };

  const remove = async () => {
    setState({ busy: 'delete', error: '' });
    try {
      await atLeast(onDelete(), LOGO_LOADER_MS);
      setState({ busy: '', error: '' });
    } catch (err) {
      setState({ busy: '', error: err.message || 'The logo could not be deleted.' });
    }
  };

  const busy = Boolean(state.busy);
  return (
    <section className="org-card orgc-org" aria-labelledby="orgc-org-title">
      <div className="orgc-org-head">
        <div>
          <h3 id="orgc-org-title">Organization on the certificates</h3>
          <p className="orgc-dim">One name and logo for every certificate of this event. They come from your organization, and changing them here updates your organization too.</p>
        </div>
        {orgChanged(org) && <span className="orgc-chip is-ready"><i className="bi bi-circle-fill" aria-hidden="true" /> Unsaved changes</span>}
      </div>

      <div className="orgc-org-body">
        <div className="orgc-field orgc-org-logo">
          <span>Organization logo <em>*</em></span>
          <div className="orgc-logo">
            <span className={`orgc-logobox is-large${logo ? '' : ' is-empty'}`}>
              {state.busy === 'upload' || state.busy === 'delete'
                ? <span className="orgc-loader" role="status" aria-label={state.busy === 'upload' ? 'Uploading logo' : 'Deleting logo'} />
                : logo ? <img src={logo} alt="" /> : <i className="bi bi-image" aria-hidden="true" />}
            </span>
            <div className="orgc-logo-actions">
              <input ref={input} type="file" accept="image/*" onChange={pick} hidden />
              <button type="button" className="org-btn ghost orgc-btn" disabled={readOnly || busy} onClick={() => input.current?.click()}>
                <i className={`bi ${state.busy === 'upload' ? 'bi-arrow-repeat orgc-spin' : 'bi-cloud-arrow-up'}`} aria-hidden="true" />
                {state.busy === 'upload' ? 'Uploading…' : (logo ? 'Replace logo' : 'Upload logo')}
              </button>
              {logo && (
                <button type="button" className="org-btn ghost orgc-btn orgc-danger" disabled={readOnly || busy} onClick={remove}>
                  <i className={`bi ${state.busy === 'delete' ? 'bi-arrow-repeat orgc-spin' : 'bi-trash3'}`} aria-hidden="true" />
                  {state.busy === 'delete' ? 'Deleting…' : 'Delete'}
                </button>
              )}
            </div>
          </div>
          <small className="orgc-hint">
            {unsavedLogo && logo
              ? 'New logo uploaded. It is saved to your organization and the certificates when you click Save.'
              : 'This logo is printed on the certificate. PNG, JPG or SVG, up to 1 MB.'}
          </small>
          {isDeletedLogo(org.logoUrl) && !errors.certificate_org_logo && (
            <small className="orgc-err">The logo was deleted. Upload a new one before saving.</small>
          )}
          {errors.certificate_org_logo && <small className="orgc-err">{errors.certificate_org_logo}</small>}
          {state.error && <small className="orgc-err" role="alert">{state.error}</small>}
        </div>

        <label className="orgc-field orgc-org-name">
          <span>Organization name <em>*</em></span>
          <input
            className="orgf-input"
            value={org.name}
            maxLength={ORGANIZATION_NAME_MAX}
            disabled={readOnly || busy}
            onChange={(e) => onChange({ name: e.target.value })}
          />
          <small className="orgc-hint">This name is printed on the certificate. · {String(org.name || '').length}/{ORGANIZATION_NAME_MAX}</small>
          {errors.certificate_org_name && <small className="orgc-err">{errors.certificate_org_name}</small>}
        </label>
      </div>
    </section>
  );
}

/** The gallery: every active template, rendered, to pick one for an activity. */
function TemplateGallery({ templates, selectedId, activityName, values: baseValues, course, onPick, onClose }) {
  const mapImage = useCertificateMap(course);
  const values = useMemo(() => withCertificateMap(baseValues, mapImage), [baseValues, mapImage]);
  const [previewing, setPreviewing] = useState(null);
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape' && !previewing) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, previewing]);

  return createPortal(
    <div className="ochd-scrim" role="presentation" onClick={onClose}>
      <div className="ochd orgc-gallery" role="dialog" aria-modal="true" aria-label="Choose a certificate template" onClick={(e) => e.stopPropagation()}>
        <header className="ochd-head">
          <div>
            <h2>Choose a certificate template</h2>
            <p className="orgc-dim">For <b>{activityName}</b>. Previews show your organization name and logo.</p>
          </div>
          <button type="button" className="ochk-iconbtn" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>
        <div className="orgc-tiles" role="listbox" aria-label="Templates">
          {templates.map((template) => {
            const selected = template.id === selectedId;
            return (
              <div key={template.id} role="option" aria-selected={selected} className={`orgc-tile${selected ? ' is-selected' : ''}`}>
                <button type="button" className="orgc-tile-pick" onClick={() => onPick(template.id)} aria-label={`Use ${template.name}`}>
                  <CertificateRender template={template} width={228} values={values} />
                </button>
                <span className="orgc-tile-name">
                  {template.name}
                  {selected && <i className="bi bi-check-circle-fill" aria-label="Selected" />}
                </span>
                {template.description && <span className="orgc-tile-desc">{template.description}</span>}
                <div className="orgc-tile-actions">
                  <button type="button" className="org-btn ghost orgc-btn" onClick={() => setPreviewing(template)}>
                    <i className="bi bi-arrows-fullscreen" aria-hidden="true" /> Preview
                  </button>
                  <button type="button" className="org-btn orgc-btn" onClick={() => onPick(template.id)}>
                    {selected ? 'Selected' : 'Use'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {previewing && (
        <CertificatePreview
          template={previewing}
          values={values}
          subtitle={`For ${activityName}, with your organization name and logo.`}
          onClose={() => setPreviewing(null)}
          onUse={() => { setPreviewing(null); onPick(previewing.id); }}
        />
      )}
    </div>,
    shell(),
  );
}

function ActivityCertificate({ activity, certificate, templates, readOnly, values: baseValues, onChoose }) {
  // The activity's real map (its course, or its venue) once it is built.
  const mapImage = useCertificateMap(courseOf(activity));
  const values = useMemo(() => withCertificateMap(baseValues, mapImage), [baseValues, mapImage]);
  const [previewing, setPreviewing] = useState(false);
  const status = STATUS[certificateStatus(certificate)];
  const template = templates.find((t) => t.id === certificate.templateId) || null;
  const locked = readOnly;

  return (
    <article className="org-card orgc-card">
      <div className="orgc-preview">
        {template ? (
          <>
            <button type="button" className="orgc-preview-open" onClick={() => setPreviewing(true)} aria-label={`Preview the ${template.name} certificate`}>
              <CertificateRender template={template} width={300} values={values} />
              <span className="orgc-preview-hover"><i className="bi bi-arrows-fullscreen" aria-hidden="true" /> Preview</span>
            </button>
            <div className="orgc-preview-actions">
              <button type="button" className="orgc-pillbtn" onClick={() => setPreviewing(true)}>
                <i className="bi bi-eye" aria-hidden="true" /> Preview
              </button>
              {!locked && (
                <button type="button" className="orgc-pillbtn" onClick={onChoose}>
                  <i className="bi bi-grid-3x2-gap" aria-hidden="true" /> Change template
                </button>
              )}
            </div>
          </>
        ) : (
          <button type="button" className="orgc-empty" onClick={onChoose} disabled={locked || templates.length === 0}>
            <i className="bi bi-award" aria-hidden="true" />
            <span>Choose a template</span>
          </button>
        )}
      </div>

      <div className="orgc-body">
        <div className="orgc-head">
          <h3>{activity.name || 'Untitled activity'}</h3>
          <span className={`orgc-chip is-${status.tone}`}>
            <i className={`bi ${status.icon}`} aria-hidden="true" /> {status.label}
          </span>
        </div>
        {template ? (
          <dl className="orgc-facts">
            <div><dt>Template</dt><dd>{template.name}</dd></div>
          </dl>
        ) : null}
        {template?.description && <p className="orgc-desc">{template.description}</p>}
        {!template && (certificate.templateId ? (
          <p className="orgc-template orgc-err">The saved template is no longer offered. Choose another.</p>
        ) : (
          <p className="orgc-template orgc-dim">No template chosen. This activity gets no certificate.</p>
        ))}
        {certificate.locked && (
          <p className="orgc-note"><i className="bi bi-info-circle" aria-hidden="true" /> Certificates have already been issued for this activity. If you change the template, or the organization name or logo, those certificates show the change the next time they are downloaded. Their numbers stay the same.</p>
        )}
      </div>
      {previewing && template && (
        <CertificatePreview
          template={template}
          values={values}
          subtitle={`${activity.name || 'Untitled activity'} · how a participant's certificate will look`}
          onClose={() => setPreviewing(false)}
        />
      )}
    </article>
  );
}

/**
 * Step 4 — Certificate templates, for an event that is already saved: one
 * organization (name and logo) for the whole event, and a template per
 * activity. Written with the rest of the event on Save draft / Publish; the
 * logo's Delete is immediate. A new event is told to save first.
 */
export default function EventStepCertificates({
  form, dispatch, organization, readOnly = false, errors = {},
  // The wizard's raw dispatch, for values the system loads or writes itself
  // (saved choices, the organization, a deleted logo): not an edit, so it must
  // not arm the unsaved-changes prompt, and it still runs on a locked event.
  systemDispatch = dispatch,
}) {
  const templates = useActiveTemplates();
  const list = templates.data || [];
  const activities = form.activities || [];
  const org = form.certificate_org;
  const saved = isSavedEvent(form);
  const [picking, setPicking] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [ownSave, setOwnSave] = useState({ busy: false, attempted: false, message: '', failed: false });
  const loadedFor = useRef(null);

  // A finished event is locked everywhere else in the wizard (and its Save
  // draft / Publish are hidden), but certificates are set up after an event
  // ends — so step 4 stays editable and saves on its own. Cancelled: read-only.
  const finished = readOnly && certificatesEditable(form);
  const stepReadOnly = readOnly && !finished;
  const edit = finished ? systemDispatch : dispatch;

  // A saved event: its organization, its saved choices and which activities are
  // locked. Once per event, and never over choices already in the form.
  useEffect(() => {
    if (!saved || loadedFor.current === form.id) return;
    loadedFor.current = form.id;
    const savedIds = activities.map((a) => a.id).filter((id) => id != null && id !== '');
    (async () => {
      try {
        const [choices, counts, organizationRow] = await Promise.all([
          fetchRows(checkInApi, CERTIFICATE_CHOICE_TABLE, { filters: [equals('event_id', form.id)], pageSize: 500 }),
          Promise.all(savedIds.map((id) => countRows(checkInApi, ISSUED_CERTIFICATE_TABLE, [
            equals('event_id', form.id), equals('event_activity_id', id),
          ]))),
          form.creator_organization_id ? fetchRowById(checkInApi, ORGANIZATION_TABLE, Number(form.creator_organization_id)) : null,
        ]);
        const issued = Object.fromEntries(savedIds.map((id, i) => [String(id), counts[i]]));
        hydrateCertificates(activities, choices, issued, idOf).forEach(({ index, certificate }) => {
          systemDispatch({ type: 'updateActivity', index, field: 'certificate', value: certificate });
        });
        if (!form.certificate_org?.loaded) {
          systemDispatch({ type: 'setField', field: 'certificate_org', value: orgFromRows(organizationRow, choices) });
        }
      } catch {
        loadedFor.current = null;
        setLoadError('The certificate settings could not be loaded. Reload the page to try again.');
      }
    })();
  }, [saved, form.id, form.creator_organization_id, form.certificate_org, activities, systemDispatch]);

  const changeOrg = (patch) => edit({ type: 'setField', field: 'certificate_org', value: { ...org, ...patch } });
  const chooseTemplate = (index, templateId) => {
    edit({ type: 'updateActivity', index, field: 'certificate', value: editCertificate(certificateOf(activities[index]), { templateId }) });
  };

  // Re-reads what is saved, so the step shows it as saved (new choices get their ids).
  const reloadSaved = async () => {
    const [choices, organizationRow] = await Promise.all([
      fetchRows(checkInApi, CERTIFICATE_CHOICE_TABLE, { filters: [equals('event_id', form.id)], pageSize: 500 }),
      form.creator_organization_id ? fetchRowById(checkInApi, ORGANIZATION_TABLE, Number(form.creator_organization_id)) : null,
    ]);
    const byActivity = new Map(choices.map((row) => [String(idOf(row.event_activity_id)), row]));
    activities.forEach((activity, index) => {
      const row = byActivity.get(String(activity.id));
      if (!row) return;
      const templateId = idOf(row.certificate_template_id);
      systemDispatch({
        type: 'updateActivity', index, field: 'certificate',
        value: { ...certificateOf(activity), choiceId: row.id, templateId: templateId == null ? '' : String(templateId), dirty: false },
      });
    });
    systemDispatch({ type: 'setField', field: 'certificate_org', value: orgFromRows(organizationRow, choices) });
  };

  // Step 4's own Save, for a finished event: only the certificate settings
  // (organization + every choice row of the event); the event is not touched.
  const ownErrors = finished ? certificateFormErrors(form) : {};
  const ownOperations = finished && org?.loaded ? certificateBatchOperations(form, form.id, (activity) => activity.id) : [];
  const saveOwn = async () => {
    if (Object.keys(ownErrors).length) { setOwnSave({ busy: false, attempted: true, message: '', failed: false }); return; }
    if (!ownOperations.length) return;
    setOwnSave({ busy: true, attempted: true, message: '', failed: false });
    try {
      await saveBatch(checkInApi, ownOperations);
      await reloadSaved();
      setOwnSave({ busy: false, attempted: false, message: 'Certificate settings saved.', failed: false });
    } catch (error) {
      setOwnSave({ busy: false, attempted: true, message: error?.message || 'The certificate settings were not saved.', failed: true });
    }
  };

  // Delete is immediate: the file leaves storage, then the organization row and
  // every saved choice of this event keep the old link behind "deleted:".
  const deleteLogo = async () => {
    const current = org.logoUrl;
    if (current !== org.savedLogoUrl) {
      // Uploaded in this visit and never saved: only its file exists.
      await deleteUploadedFile(current);
      systemDispatch({ type: 'setField', field: 'certificate_org', value: { ...org, logoUrl: org.savedLogoUrl } });
      return;
    }
    const removed = await deleteUploadedFile(current);
    if (!removed) throw new Error('The logo could not be deleted from storage. Please try again.');
    const { operations, marked } = deleteLogoOperations(org);
    if (operations.length) await saveBatch(checkInApi, operations);
    systemDispatch({ type: 'setField', field: 'certificate_org', value: { ...org, logoUrl: marked, savedLogoUrl: marked } });
  };

  if (!saved) {
    return (
      <section className="org-card orgc-intro orgc-gate" role="status">
        <div className="orgc-intro-icon" aria-hidden="true"><i className="bi bi-hourglass-split" /></div>
        <div>
          <p className="org-lab">E-certificates</p>
          <h2 className="orgc-title">Save your event first</h2>
          <p className="orgc-dim">
            Certificate templates become available once the event and its activities are created. Click <b>Back</b> and
            finish the first three steps, then <b>Save draft</b> or <b>Publish</b>. Open the event again to choose its certificates.
          </p>
        </div>
      </section>
    );
  }

  const configured = activities.filter((a) => ['saved', 'ready', 'issued'].includes(certificateStatus(certificateOf(a)))).length;
  const orgValues = { organizationName: org?.name || organization?.organizationName || '', logoUrl: org?.logoUrl || '' };

  return (
    <>
      <section className="org-card orgc-intro">
        <div className="orgc-intro-icon" aria-hidden="true"><i className="bi bi-award" /></div>
        <div>
          <p className="org-lab">E-certificates</p>
          <h2 className="orgc-title">Choose the certificate for each activity</h2>
          <p className="orgc-dim">
            Participants who check in can be given a certificate from the Participants page. Pick a design for each activity.
            This step is optional: an activity without a design gets no certificate.
          </p>
        </div>
        {activities.length > 0 && (
          <div className="orgc-progress" aria-label={`${configured} of ${activities.length} activities set`}>
            <b>{configured}</b><span>/ {activities.length}</span>
            <small>activities set</small>
          </div>
        )}
      </section>

      {templates.isError && (
        <div className="orgf-notice" role="alert">
          <span>The certificate templates could not be loaded, so certificates can&rsquo;t be set up right now. The rest of the event can still be saved.</span>
          <button type="button" className="org-btn ghost" style={{ marginLeft: 'auto' }} onClick={() => templates.refetch()}>Retry</button>
        </div>
      )}
      {templates.isSuccess && list.length === 0 && (
        <div className="orgf-notice" role="status"><span>No certificate templates are available yet. Ask the AthloHive team to add one.</span></div>
      )}
      {loadError && <div className="orgf-notice" role="alert"><span>{loadError}</span></div>}

      {!org?.loaded && !loadError && <section className="org-card"><p className="orgc-dim" style={{ margin: 0 }}>Loading certificate settings…</p></section>}

      {finished && (
        <div className="orgf-notice orgc-finished" role="status">
          <span>
            <b>This event has finished.</b> Its other steps can no longer be changed, but you can still set up and change its
            certificates here. Use <b>Save certificate settings</b> below.
          </span>
        </div>
      )}

      {org?.loaded && (
        <OrganizationCard
          org={org}
          readOnly={stepReadOnly}
          errors={finished ? (ownSave.attempted ? ownErrors : {}) : errors}
          onChange={changeOrg}
          onDelete={deleteLogo}
        />
      )}

      {org?.loaded && list.length > 0 && activities.map((activity, index) => (
        <ActivityCertificate
          key={activity.id ?? `new-${index}`}
          activity={activity}
          certificate={certificateOf(activity)}
          templates={list}
          readOnly={stepReadOnly}
          values={previewValues({ form, activity, certificate: orgValues, organizer: orgValues.organizationName })}
          onChoose={() => setPicking(index)}
        />
      ))}

      {finished && org?.loaded && (
        <div className="org-card orgc-ownsave">
          <span className={`orgc-ownsave-msg${ownSave.failed ? ' orgc-err' : ''}`} role="status">
            {ownSave.message || (ownOperations.length ? 'You have unsaved certificate changes.' : 'No unsaved changes.')}
            {ownSave.attempted && Object.keys(ownErrors).length > 0 && ' Fix the highlighted fields first.'}
          </span>
          <button type="button" className="org-btn" onClick={saveOwn} disabled={ownSave.busy || !ownOperations.length}>
            <i className={`bi ${ownSave.busy ? 'bi-arrow-repeat orgc-spin' : 'bi-check2'}`} aria-hidden="true" />
            {ownSave.busy ? 'Saving…' : 'Save certificate settings'}
          </button>
        </div>
      )}

      {picking != null && activities[picking] && (
        <TemplateGallery
          templates={list}
          activityName={activities[picking].name || 'Untitled activity'}
          selectedId={certificateOf(activities[picking]).templateId}
          values={previewValues({ form, activity: activities[picking], certificate: orgValues, organizer: orgValues.organizationName })}
          course={courseOf(activities[picking])}
          onPick={(templateId) => { chooseTemplate(picking, templateId); setPicking(null); }}
          onClose={() => setPicking(null)}
        />
      )}
    </>
  );
}
